import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import { AuthProvider } from "./contexts/AuthContext";
import "./index.css";
import { persister, queryClient } from "./lib/queryClient";
import reportWebVitals from "./reportWebVitals";

function Root() {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    // Restore cache from IndexedDB on mount (async)
    persister.restoreClient().then(() => {
      setIsHydrated(true);
    });

    // Debounce persistence to avoid saving on every keystroke
    // Wait 2 seconds after last change before persisting
    let persistTimeout: NodeJS.Timeout | null = null;

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      if (persistTimeout) {
        clearTimeout(persistTimeout);
      }
      persistTimeout = setTimeout(() => {
        persister.persistClient(queryClient);
      }, 2000); // Wait 2 seconds after last change
    });

    return () => {
      unsubscribe();
      if (persistTimeout) {
        clearTimeout(persistTimeout);
        // Persist one final time on unmount to avoid data loss
        persister.persistClient(queryClient);
      }
    };
  }, []);

  if (!isHydrated) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading cache...</span>
        </div>
      </div>
    );
  }

  return (
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <App />
        </AuthProvider>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>
  );
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(<Root />);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
